import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { crmMastersQueryKey, getCrmMasterByCustomer, type CrmMasterDetail } from "@/lib/api/crm/masters";
import { applyCrmRecordChanges } from "@/lib/api/crm/records";
import { crmRulesQueryKey, fetchCrmRules } from "@/lib/api/crm/rules";
import { baseCustomersQueryKey } from "@/lib/api/customer";
import { ApplyCrmRecordChangesRequestValidation } from "@/lib/validation/crm";
import { crmErrorReason } from "./crmErrors";
import { toFormSubmitError } from "./form/recordFormErrors";
import { recordTypeFields, type RecordFormTypeFields } from "./form/recordFormSchema";
import { hasRecordChanges, toApplyChangesRequest, toRecordFormValues } from "./form/recordFormValues";
import { recordFieldLabels } from "./recordFields";

export type CrmCustomerChangeResult = { success: true } | { success: false; message: string };

type QueryKey = readonly unknown[];

const failed = (message: string): CrmCustomerChangeResult => ({ success: false, message });

async function applyCustomerChange(
  queryClient: QueryClient,
  refreshQueryKeys: readonly QueryKey[],
  customerId: string,
  fields: Partial<RecordFormTypeFields>,
  expected: Partial<RecordFormTypeFields>,
): Promise<CrmCustomerChangeResult> {
  const rules = await queryClient.fetchQuery({ queryKey: crmRulesQueryKey, queryFn: fetchCrmRules });
  const masterResult = await getCrmMasterByCustomer(customerId);
  if (!masterResult.success) return failed(masterResult.message || "This customer's CRM record couldn't be loaded.");
  const master: CrmMasterDetail = masterResult.data;
  const record = master.records.find((candidate) => candidate.customer_id === customerId);
  if (!record) return failed("This customer's CRM record couldn't be found.");

  const typeFields = recordTypeFields(rules, record.record_type);
  const missing = (Object.keys({ ...fields, ...expected }) as (keyof RecordFormTypeFields)[])
    .filter((field) => !typeFields.includes(field));
  if (missing.length > 0) {
    return failed(`This customer's CRM record type doesn't have: ${missing.map((field) => recordFieldLabels[field]).join(", ")}.`);
  }

  const original = toRecordFormValues(rules, record);
  const changedSince = (Object.keys(expected) as (keyof RecordFormTypeFields)[])
    .filter((field) => JSON.stringify(original.fields[field]) !== JSON.stringify(expected[field]));
  if (changedSince.length > 0) return failed("This customer's CRM record has changed. Refresh and try again.");
  const values = { ...original, fields: { ...original.fields, ...fields } };
  if (!hasRecordChanges(rules, record, original, values)) return { success: true };

  const parsed = ApplyCrmRecordChangesRequestValidation.safeParse(toApplyChangesRequest(rules, record, original, values).request);
  if (!parsed.success) return failed(parsed.error.issues.map((issue) => issue.message).join(" "));

  const result = await applyCrmRecordChanges(parsed.data);
  void queryClient.invalidateQueries({ queryKey: crmMastersQueryKey });
  if (!result.success) {
    // The record was loaded just before saving, so a conflict means a save landed in between.
    if (crmErrorReason(result.data) === "crm_record_revision_conflict") {
      return failed("This customer's CRM record was saved by someone else at the same time. Try again.");
    }
    // Otherwise the same messages the form shows; there are no fields to put them on here.
    const error = toFormSubmitError(rules, result, values, {});
    return failed([error.banner?.message, ...error.fieldErrors.map((fieldError) => fieldError.message)].filter(Boolean).join(" ")
      || result.message || "The change couldn't be saved.");
  }
  void queryClient.invalidateQueries({ queryKey: baseCustomersQueryKey });
  for (const queryKey of refreshQueryKeys) void queryClient.invalidateQueries({ queryKey });
  return { success: true };
}

/**
 * Sets type fields on the CRM record linked to a customer, without a form: loads the record fresh (so the
 * revision is current), applies the fields to its stored values and saves with apply-changes. With
 * `expected`, nothing is saved unless the record still has those values. The customer row is synced by the
 * same CRM write, so the nx customer lists (and any `refreshQueryKeys`) are reloaded. Never throws: every
 * failure comes back as `{ success: false, message }`.
 */
export function useApplyCrmCustomerChange(refreshQueryKeys: readonly QueryKey[] = []) {
  const queryClient = useQueryClient();

  return async (
    customerId: string,
    fields: Partial<RecordFormTypeFields>,
    expected: Partial<RecordFormTypeFields> = {},
  ): Promise<CrmCustomerChangeResult> => {
    try {
      return await applyCustomerChange(queryClient, refreshQueryKeys, customerId, fields, expected);
    } catch (error: unknown) {
      return failed(error instanceof Error ? error.message : "The change couldn't be saved.");
    }
  };
}
